import { useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  AccountCircleOutlined,
  AdminPanelSettingsOutlined,
  ChevronRight,
  GroupsOutlined,
  HelpOutlineOutlined,
  HistoryOutlined,
  LogoutOutlined,
  SettingsOutlined,
  SwapHorizOutlined,
} from '@mui/icons-material';
import { Avatar, Divider } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { companiesAction, fundsAction, globalAction } from 'common/actions';
import { firmAuditLogsUrl, firmCompGroupsUrl, firmIntelligenceUrl, firmUserManagementUrl, userProfileUrl } from 'common/config/urls';
import { CREATE_FIRM_ENABLER } from 'common/constants/features';
import { FIRM_ADMIN_VALUE, VALID_ROLES_FOR_AUDIT_LOGS } from 'common/constants/user';
import { useFirmScopedPermissions } from 'common/hooks';
import { useStore } from 'common/store';
import type { Firm } from 'common/types/firm';
import type { UseStoreValues } from 'common/types/store';
import useCheckFeatures from 'components/FeatureEnabler/hooks';
import {
  PrimaryNavigationMenu,
  PrimaryNavigationMenuItem,
  useGuidedTour,
} from 'components/ui';
import { FIRM_PRIMARY_NAVIGATION_TEST_IDS } from 'layouts/FirmBaseLayout/PrimaryNavigation/PrimaryNavigation.testIds';
import { useSignOutAction } from 'pages/Auth/hooks';
import { getInitials } from 'utilities/getInitials';
import FirmSettingsMenu from './FirmSettingsMenu';
import FirmSwitcherMenu from './FirmSwitcherMenu';
import type { PrimaryNavigationOptionsMenuProps } from './PrimaryNavigationOptionsMenu.props';
import ProductToursMenu from './ProductToursMenu';

interface PrimaryNavigationOptionsMenuSection {
  dividerBefore?: boolean;
  items: ReactNode[];
  key: string;
}

// MUI Menu warns on Fragment children; keep sections flattened as direct MenuItem/Divider nodes.
const renderMenuSections = (sections: PrimaryNavigationOptionsMenuSection[]) =>
  sections.flatMap(section => {
    if (section.items.length === 0) {
      return [];
    }

    return [
      ...(section.dividerBefore ? [<Divider key={`${section.key}-divider`} />] : []),
      ...section.items,
    ];
  });

const PrimaryNavigationOptionsMenu = ({
  anchorEl,
  isGlobalCompGroupsRoute = false,
  onClose,
  zoomLevel,
}: PrimaryNavigationOptionsMenuProps) => {
  const { firmSlugParam } = useParams<{ firmSlugParam: string }>();
  const navigate = useNavigate();
  const [{firmList, user}, dispatch] = useStore() as unknown as UseStoreValues;
  const [, checkFeature] = useCheckFeatures();
  const [switchFirmAnchorEl, setSwitchFirmAnchorEl] = useState<HTMLElement | null>(null);
  const [firmSettingsAnchorEl, setFirmSettingsAnchorEl] = useState<HTMLElement | null>(null);
  const [productToursAnchorEl, setProductToursAnchorEl] = useState<HTMLElement | null>(null);
  const switchFirmItemRef = useRef<HTMLLIElement>(null);
  const firmSettingsItemRef = useRef<HTMLLIElement>(null);
  const productToursItemRef = useRef<HTMLLIElement>(null);
  const { currentFirmRole, isRestrictedUser, isViewOnlyUser } = useFirmScopedPermissions();
  const { availableTours, startTour } = useGuidedTour();

  const firms = useMemo(() => (firmList as Firm[] | null | undefined) ?? [], [firmList]);

  const activeFirm = useMemo(
    () => firms.find(firm => firm.slug === firmSlugParam) ?? null,
    [firms, firmSlugParam]
  );
  const firmScopedSlug = isGlobalCompGroupsRoute ? undefined : activeFirm?.slug;
  const hasActiveFirmContext = Boolean(firmScopedSlug);

  const handleSwitchFirmOpen = () => {
    setSwitchFirmAnchorEl(switchFirmItemRef.current);
  };

  const handleSwitchFirmClose = () => {
    setSwitchFirmAnchorEl(null);
  };

  const handleFirmSettingsOpen = () => {
    setFirmSettingsAnchorEl(firmSettingsItemRef.current);
  };

  const handleFirmSettingsClose = () => {
    setFirmSettingsAnchorEl(null);
  };

  const handleProductToursOpen = () => {
    setProductToursAnchorEl(productToursItemRef.current);
  };

  const handleProductToursClose = () => {
    setProductToursAnchorEl(null);
  };

  const handleFirmSelect = (firm: Firm) => {
    dispatch(companiesAction.setCompanyList(null));
    dispatch(fundsAction.setFundList(null));
    navigate(firmIntelligenceUrl(firm.slug));
    handleSwitchFirmClose();
    onClose();
  };

  const handleClose = () => {
    handleSwitchFirmClose();
    handleFirmSettingsClose();
    handleProductToursClose();
    onClose();
  };

  const handleTourSelect = (tourId: string) => {
    handleClose();
    startTour(tourId);
  };

  const isSuperuser = Boolean(user?.is_superuser);
  const hasRole = (check: (role: number) => boolean) => isSuperuser || (currentFirmRole !== null && check(currentFirmRole));

  const canSeeAuditLogs = hasRole(role => VALID_ROLES_FOR_AUDIT_LOGS.has(role));
  const canSeeFirmSettings = hasRole(role => role === FIRM_ADMIN_VALUE);
  const canSeeUserManagement = !isRestrictedUser && (isSuperuser || !isViewOnlyUser);

  const canAddFirm = Boolean(user?.is_superuser || checkFeature(CREATE_FIRM_ENABLER));
  const showSwitchFirm = firms.length > 1 || canAddFirm;

  const handleAccountSettingsClick = () => {
    if (!firmScopedSlug) {
      return;
    }

    navigate(userProfileUrl(firmScopedSlug));
    handleClose();
  };

  const handleCompGroupsClick = () => {
    if (!firmScopedSlug) {
      return;
    }

    navigate(firmCompGroupsUrl(firmScopedSlug));
    handleClose();
  };

  const handleUserManagementClick = () => {
    if (!firmScopedSlug) {
      return;
    }

    navigate(firmUserManagementUrl(firmScopedSlug));
    handleClose();
  };

  const handleAuditLogsClick = () => {
    if (!firmScopedSlug) {
      return;
    }

    navigate(firmAuditLogsUrl(firmScopedSlug));
    handleClose();
  };

  const handleAddFirm = () => {
    handleClose();
    dispatch(globalAction.showFirmStepper(true));
  };

  const handleSignOut = useSignOutAction({ onBeforeSignOut: handleClose });

  return (
    <>
      <PrimaryNavigationMenu
        id={FIRM_PRIMARY_NAVIGATION_TEST_IDS.optionsMenu}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.optionsMenu}
        menuMinWidth={200}
        zoomLevel={zoomLevel}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        {renderMenuSections([
          {
            key: 'active-firm',
            items: activeFirm ? [
              <PrimaryNavigationMenuItem
                key="active-firm"
                disabled
                isHeader
                leadingSlot={(
                  <Avatar
                    alt={`${activeFirm.name} firm icon`}
                    src={activeFirm.icon_url ?? undefined}
                    variant="rounded"
                    sx={{ width: 16, height: 16, borderRadius: '4px', fontSize: 7 }}>
                    {getInitials({ name: activeFirm.name })}
                  </Avatar>
                )}>
                {activeFirm.name}
              </PrimaryNavigationMenuItem>,
            ] : [],
          },
          {
            key: 'account-settings',
            items: hasActiveFirmContext ? [
              <PrimaryNavigationMenuItem
                key="account-settings"
                icon={<AccountCircleOutlined fontSize="inherit" />}
                onClick={handleAccountSettingsClick}
                data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.accountSettings}>
                Account Settings
              </PrimaryNavigationMenuItem>,
            ] : [],
          },
          {
            key: 'switch-firm',
            items: showSwitchFirm ? [
              <PrimaryNavigationMenuItem
                key="switch-firm"
                ref={switchFirmItemRef}
                icon={<SwapHorizOutlined fontSize="inherit" />}
                isActive={Boolean(switchFirmAnchorEl)}
                onClick={handleSwitchFirmOpen}
                endIcon={<ChevronRight fontSize="small" />}
                data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.switchFirm}>
                Switch Firm
              </PrimaryNavigationMenuItem>,
            ] : [],
          },
          {
            key: 'user-management',
            items: canSeeUserManagement && hasActiveFirmContext ? [
              <PrimaryNavigationMenuItem
                key="user-management"
                icon={<AdminPanelSettingsOutlined fontSize="inherit" />}
                onClick={handleUserManagementClick}
                data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.userManagement}>
                User Management
              </PrimaryNavigationMenuItem>,
            ] : [],
          },
          {
            key: 'comp-groups',
            items: canSeeFirmSettings && hasActiveFirmContext ? [
              <PrimaryNavigationMenuItem
                key="comp-groups"
                icon={<GroupsOutlined fontSize="inherit" />}
                onClick={handleCompGroupsClick}
                data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.compGroups}>
                Comp Groups
              </PrimaryNavigationMenuItem>,
            ] : [],
          },
          {
            key: 'audit-logs',
            dividerBefore: true,
            items: canSeeAuditLogs && hasActiveFirmContext ? [
              <PrimaryNavigationMenuItem
                key="audit-logs"
                icon={<HistoryOutlined fontSize="inherit" />}
                onClick={handleAuditLogsClick}
                data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.auditLogs}>
                Audit Logs
              </PrimaryNavigationMenuItem>,
            ] : [],
          },
          {
            key: 'firm-settings',
            items: canSeeFirmSettings && hasActiveFirmContext ? [
              <PrimaryNavigationMenuItem
                key="firm-settings"
                ref={firmSettingsItemRef}
                icon={<SettingsOutlined fontSize="inherit" />}
                isActive={Boolean(firmSettingsAnchorEl)}
                onClick={handleFirmSettingsOpen}
                endIcon={<ChevronRight fontSize="small" />}
                data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.firmSettings}>
                Firm Settings
              </PrimaryNavigationMenuItem>,
            ] : [],
          },
          {
            key: 'product-tours',
            dividerBefore: true,
            items: hasActiveFirmContext ? [
              <PrimaryNavigationMenuItem
                key="product-tours"
                ref={productToursItemRef}
                icon={<HelpOutlineOutlined fontSize="inherit" />}
                isActive={Boolean(productToursAnchorEl)}
                onClick={handleProductToursOpen}
                endIcon={<ChevronRight fontSize="small" />}
                data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.productTours}
                data-tour="guided-tour-help-menu">
                Guided Tours
              </PrimaryNavigationMenuItem>,
            ] : [],
          },
          {
            key: 'sign-out',
            dividerBefore: isGlobalCompGroupsRoute,
            items: [
              <PrimaryNavigationMenuItem
                key="sign-out"
                icon={<LogoutOutlined fontSize="inherit" />}
                onClick={handleSignOut}
                data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.signOut}>
                Sign Out
              </PrimaryNavigationMenuItem>,
            ],
          },
        ])}
      </PrimaryNavigationMenu>

      <FirmSwitcherMenu
        activeFirmSlug={firmSlugParam}
        anchorEl={switchFirmAnchorEl}
        canAddFirm={canAddFirm}
        firms={firms}
        onAddFirm={handleAddFirm}
        onClose={handleSwitchFirmClose}
        onFirmSelect={handleFirmSelect}
        zoomLevel={zoomLevel}
      />

      {firmScopedSlug && (
        <ProductToursMenu
          anchorEl={productToursAnchorEl}
          onClose={handleProductToursClose}
          onTourSelect={handleTourSelect}
          tours={availableTours}
          zoomLevel={zoomLevel}
        />
      )}

      {firmScopedSlug && (
        <FirmSettingsMenu
          anchorEl={firmSettingsAnchorEl}
          firmSlug={firmScopedSlug}
          onClose={handleFirmSettingsClose}
          onNavigate={
            url => {
              navigate(url);
              handleClose();
            }
          }
          zoomLevel={zoomLevel}
        />
      )}
    </>
  );
};

export default PrimaryNavigationOptionsMenu;
